import { createSelector, createSlice, current } from '@reduxjs/toolkit';
import scriptItemData from '../../public/scriptitems.json';
import { deriveScriptMetadata } from './ScriptParser';


// ==========================================================
// Slice 定義
// ==========================================================
const initialState = {
  scripts: [],
  trashbox: []
};


const scriptItemSlice = createSlice({
  name: 'script',
  initialState,
  reducers: {
    loadScriptData(state, action) {
      const payload = action.payload;

      if (!payload || typeof payload !== 'object') {
        const defaultScripts = Array.isArray(scriptItemData) ? scriptItemData : [];
        state.scripts = defaultScripts.map(s => ({ scriptType: 'function', comment: '', ...s }));
        state.trashbox = [];
        return;
      }

      const rawScripts = Array.isArray(payload.scripts) ? payload.scripts : [];
      const rawTrashbox = Array.isArray(payload.trashbox) ? payload.trashbox : [];

      const normalizeScript = (script) => ({
        scriptName: script.scriptName || 'untitled',
        // 【念のための強化】行データは文字列のみを通す
        scriptValue: Array.isArray(script.scriptValue)
          ? script.scriptValue.filter(line => typeof line === 'string')
          : [],
        scriptType: script.scriptType || 'function',
        comment: script.comment || ''
      });

      state.scripts = rawScripts
        .filter(s => s && typeof s === 'object')
        .map(normalizeScript);

      state.trashbox = rawTrashbox
        .filter(s => s && typeof s === 'object')
        .map(normalizeScript);
    },

    addNewScript: {
      prepare(payload) {
        const { scriptName, scriptType, scriptContent, comment } = payload;
        let scriptValue;

        // 手動追加時など、コンテンツが空の場合はテンプレートを生成
        if (!scriptContent || (Array.isArray(scriptContent) && scriptContent.length === 0)) {
          if (scriptType === 'class') {
            scriptValue = [
              `/**`,
              ` * ${scriptName}の説明`,
              ` */`,
              `class ${scriptName} {`,
              `  constructor() {`,
              `    // コンストラクタ`,
              `  }`,
              ``,
              `  myMethod() {`,
              `    // メソッド`,
              `  }`,
              `}`
            ];
          } else { // default 'function'
            const eventName = payload.target || '';
            if (eventName === '') {
              scriptValue = [
                `/**`,
                ` * ${scriptName}の説明`,
                ` */`,
                `function ${scriptName}() {`,
                `    `,
                `}`
              ];
            } else {
              scriptValue = [
                `/**`,
                ` * ${scriptName}の説明`,
                ` * @param {Event} ${eventName}`,
                ` */`,
                `function ${scriptName}(${eventName}) {`,
                `    `,
                `}`
              ];
            }
          }
        } else {
          // コンテンツがある場合（AI生成時など）はそれを使用
          scriptValue = Array.isArray(scriptContent) ? scriptContent : scriptContent.split('\n');
        }

        // 生成または渡されたコードを解析してメタデータを同期する
        const codeString = scriptValue.join('\n');
        const meta = deriveScriptMetadata(codeString);

        return {
          payload: {
            scriptName: meta?.name || scriptName,
            scriptType: meta?.type || scriptType || 'function',
            scriptValue: scriptValue,
            // meta.commentがあればそれ（JSDoc中身）を、 lazyならば引数のcommentを、それもなければデフォルト
            comment: meta?.comment || comment || `${scriptName}の説明`
          }
        };
      },
      reducer(state, action) {
        const newScript = action.payload;

        // 1. スクリプト名の入力値バリデーションとサニタイズ
        const cleanName = newScript.scriptName ? String(newScript.scriptName).trim() : '';

        // 空文字、またはパースエラーによるリテラル崩れの "undefined", "null" を不正な名前として徹底防御
        if (!cleanName || cleanName === 'undefined' || cleanName === 'null') {
          throw new Error('invalid scriptName');
        }

        // 2. スクリプトコード（中身）のバリデーション
        // scriptValueが配列ではない、またはすべての行が空白文字である（実質空）場合は登録を遮断
        const hasContent = Array.isArray(newScript.scriptValue) &&
          newScript.scriptValue.some(line => typeof line === 'string' && line.trim() !== '');

        if (!hasContent) {
          throw new Error('invalid scriptContent');
        }

        // 3. 【上書きポリシー】有効なスクリプト一覧に同名が既に存在するか確認
        const existingIndex = state.scripts.findIndex(s => s.scriptName === cleanName);
        if (existingIndex > -1) {
          // すでに有効なスクリプトとして存在している場合は、エラーにせず最新データで上書きする
          state.scripts[existingIndex] = {
            ...newScript,
            scriptName: cleanName
          };
          return;
        }

        // 4. 【ゴミ箱破棄ポリシー】ゴミ箱内に同名が眠っているか確認
        const trashIndex = state.trashbox.findIndex(s => s.scriptName === cleanName);
        if (trashIndex > -1) {
          // ゴミ箱の不要な同名残骸を自動的に完全消去する
          state.trashbox.splice(trashIndex, 1);
        }

        // 5. 通常通り新規作成して有効リストにアタッチ
        state.scripts.push({
          ...newScript,
          scriptName: cleanName
        });
      }
    },

    updateScriptItem: {
      prepare(payload) {
        const { originScriptName, scriptValue, scriptType, comment } = payload;
        const codeString = Array.isArray(scriptValue) ? scriptValue.join('\n') : scriptValue;

        // 共通パーサーを使用してメタデータを抽出
        const meta = deriveScriptMetadata(codeString);

        return {
          payload: {
            originScriptName,
            // 解析結果があればそれを新しい名前として採用（リネーム対応）
            newScriptName: meta?.name || originScriptName,
            scriptValue: Array.isArray(scriptValue) ? scriptValue : codeString.split('\n'),
            scriptType: meta?.type || scriptType || 'function',
            comment: meta?.comment || comment || ""
          }
        };
      },
      reducer(state, action) {
        const { originScriptName, newScriptName, scriptValue, scriptType, comment } = action.payload;

        // 名前変更時の重複チェック
        if (originScriptName !== newScriptName) {
          const isDuplicate = state.scripts.some(s => s.scriptName === newScriptName && s.scriptName !== originScriptName) ||
            state.trashbox.some(s => s.scriptName === newScriptName);
          if (isDuplicate) {
            throw new Error('scriptName already exists');
          }
        }

        const currentIndex = state.scripts.findIndex(script => script.scriptName === originScriptName);
        if (currentIndex !== -1) {
          state.scripts[currentIndex] = {
            scriptName: newScriptName,
            scriptValue: scriptValue,
            scriptType: scriptType,
            comment: comment
          };
        }
      }
    },

    deleteScript(state, action) {
      const scriptName = action.payload;
      const index = state.scripts.findIndex(script => script.scriptName === scriptName);
      if (index > -1) {
        state.trashbox.unshift(state.scripts[index]);
        state.scripts.splice(index, 1);
      }
    },

    // 直接削除（AIエージェント用）
    deleteScriptPermanently(state, action) {
      const scriptName = action.payload;
      const index = state.scripts.findIndex(script => script.scriptName === scriptName);
      if (index > -1) {
        state.scripts.splice(index, 1);
      }
    },

    returnScript(state, action) {
      const scriptName = action.payload;
      const index = state.trashbox.findIndex(script => script.scriptName === scriptName);
      if (index > -1) {
        state.scripts.push(state.trashbox[index]);
        state.trashbox.splice(index, 1);
      }
    },

    clearTrashbox(state, action) {
      state.trashbox = [];
    },

    changeScriptOrder(state, action) {
      const { scriptName, newIndex } = action.payload;
      const currentIndex = state.scripts.findIndex(script => script.scriptName === scriptName);
      if (currentIndex !== -1) {
        const [movedItem] = state.scripts.splice(currentIndex, 1);
        state.scripts.splice(newIndex, 0, movedItem);
      }
    }
  },
});

const selectScripts = state => state.scriptItems.scripts;

export const selectScriptByName = createSelector(
  [selectScripts, (state, scriptName) => scriptName],
  (scripts, scriptName) => scripts.find(script => script.scriptName === scriptName)
);

export const {
  loadScriptData,
  addNewScript,
  deleteScript,
  returnScript,
  clearTrashbox,
  changeScriptOrder,
  updateScriptItem,
  deleteScriptPermanently
} = scriptItemSlice.actions;

export default scriptItemSlice.reducer;